import { useTranslation } from "react-i18next";
import React from "react";
import { sanitizeInput, trimInput } from "src/helpers/sanitizeInput";

export type SaveSpec = { onSave: () => void; saved: boolean; dirty?: boolean };

// ── SettingRow ────────────────────────────────────────────────────────────────
// Full-width row: left = title+description, right = control

interface SettingRowProps {
    title: React.ReactNode;
    description?: string;
    children: React.ReactNode;
    column?: boolean;
    disabled?: boolean;
    className?: string;
}

export function SettingRow({
    title,
    description,
    children,
    column,
    disabled,
    className,
}: SettingRowProps) {
    return (
        <div className={`setting-row${column ? " setting-row--column" : ""}${disabled ? " setting-row--disabled" : ""}${className ? ` ${className}` : ""}`}>
            <div className="setting-row__info">
                <span className="setting-row__title">{title}</span>
                {description && (
                    <span className="setting-row__desc">{description}</span>
                )}
            </div>
            <div className="setting-row__control">{children}</div>
        </div>
    );
}

// ── TextInput ─────────────────────────────────────────────────────────────────

export function TextInput({
    value,
    onChange,
    onBlur,
    placeholder,
    label,
    type = "text",
    sanitize = true,
}: {
    value: string;
    onChange: (v: string) => void;
    onBlur?: (v: string) => void;
    placeholder?: string;
    label?: string;
    type?: string;
    /** Disable for fields like passwords/API keys where arbitrary special characters are valid. */
    sanitize?: boolean;
}) {
    return (
        <input
            type={type}
            className="settings-input"
            aria-label={label}
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(sanitize ? sanitizeInput(e.target.value) : e.target.value)}
            onBlur={(e) => (onBlur ?? onChange)(sanitize ? trimInput(e.target.value) : e.target.value)}
        />
    );
}

// ── TextArea ──────────────────────────────────────────────────────────────────

export function TextArea({
    value,
    onChange,
    placeholder,
    label,
    rows = 8,
    disabled,
}: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    label?: string;
    rows?: number;
    disabled?: boolean;
}) {
    return (
        <textarea
            className="settings-textarea"
            aria-label={label}
            value={value}
            placeholder={placeholder}
            rows={rows}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
        />
    );
}

// ── NumberInput ───────────────────────────────────────────────────────────────

export function NumberInput({
    value,
    onChange,
    onBlur,
    min = 1,
    max = 99,
    label,
}: {
    value: number;
    onChange: (v: number) => void;
    onBlur?: (v: number) => void;
    min?: number;
    max?: number;
    label?: string;
}) {
    return (
        <input
            className="settings-input settings-input--number"
            type="number"
            aria-label={label}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            onBlur={(e) => onBlur?.(Number(e.target.value))}
        />
    );
}

// ── UnitSelect ────────────────────────────────────────────────────────────────

export function UnitSelect({
    value,
    onChange,
}: {
    value: "days" | "weeks";
    onChange: (v: "days" | "weeks") => void;
}) {
    const { t } = useTranslation();
    return (
        <select
            className="settings-select"
            value={value}
            onChange={(e) => onChange(e.target.value as "days" | "weeks")}
        >
            <option value="days">{t("settings.daysUnit")}</option>
            <option value="weeks">{t("settings.weeksUnit")}</option>
        </select>
    );
}

// ── Toggle ────────────────────────────────────────────────────────────────────

export function Toggle({
    value,
    onChange,
    disabled,
    ariaLabel,
    ariaLabelledBy,
}: {
    value: boolean;
    onChange: (v: boolean) => void;
    disabled?: boolean;
    /** Accessible name for the switch. Most callers show a visible label right next to
     *  the toggle (a <span> or similar) — pass its id as ariaLabelledBy rather than
     *  duplicating the text into ariaLabel, so the visible and accessible names never
     *  drift apart. Use ariaLabel only when there's no visible label to point at. */
    ariaLabel?: string;
    ariaLabelledBy?: string;
}) {
    return (
        <button
            className={`toggle ${value ? "toggle--on" : ""}${disabled ? " toggle--disabled" : ""}`}
            onClick={() => !disabled && onChange(!value)}
            aria-checked={value}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            role="switch"
            disabled={disabled}
        >
            <span className="toggle__thumb" />
        </button>
    );
}

// ── SaveButton ────────────────────────────────────────────────────────────────

export function SaveButton({
    onClick,
    saved,
    dirty = true,
}: {
    onClick: () => void;
    saved: boolean;
    dirty?: boolean;
}) {
    const { t } = useTranslation();
    return (
        <button
            className={`btn-outline ${saved ? "btn-outline--saved" : ""}`}
            onClick={onClick}
            disabled={saved || !dirty}
        >
            {saved ? (
                <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {t("settings.saved")}
                </>
            ) : (
                t("settings.saveChanges")
            )}
        </button>
    );
}

// ── Section ───────────────────────────────────────────────────────────────────

export function Section({
    title,
    description,
    children,
    actions,
    sticky: _sticky = true,
    bodyClassName = "",
    logo = false,
}: {
    title: string;
    description?: string;
    children: React.ReactNode;
    actions?: React.ReactNode;
    /** @deprecated sticky titles are removed; this prop is a no-op kept for compatibility */
    sticky?: boolean;
    bodyClassName?: string;
    logo?: boolean;
}) {
    return (
        <div className="settings-section">
            <h2 className="settings-section__title">
                <div className="settings-section__title-text">
                    {logo
                        ? <img src="/medinvoice-logo.png" alt={title} className="settings-brand-logo" />
                        : <span>{title}</span>
                    }
                    {description && <p className="settings-section__description">{description}</p>}
                </div>
                {actions && (
                    <div className="settings-section__actions">{actions}</div>
                )}
            </h2>
            <div className={`settings-section__body${bodyClassName ? ` ${bodyClassName}` : ""}`}>{children}</div>
        </div>
    );
}
